import { useMemo } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from "recharts";
import { ChartTooltip } from "./ChartTooltip";
import { rampUp } from "../lib/rampup";
import { dayLabel } from "../lib/pickerSheet";
import { COLORS, fmtNum, slug } from "../lib/format";

const MAX_WEEKS = 26;

export const NewStarters = ({ raw, cfg, onOpen }) => {
  const { teamAvg, starters, cut } = useMemo(() => rampUp(raw, cfg), [raw, cfg]);
  if (!starters.length) return <div className="panel p-6 text-sm text-slate-400" data-testid="new-starters-empty">No new starters found (pickers whose first pick is 3+ weeks after the data begins).</div>;
  const shown = starters.slice(0, COLORS.length);
  const chart = Array.from({ length: Math.min(MAX_WEEKS, Math.max(...shown.map((s) => s.weeks.length))) }, (_, i) => {
    const row = { label: `Wk ${i + 1}` };
    shown.forEach((s) => { const v = s.weeks[i]?.vph; row[s.id] = v == null ? null : +v.toFixed(1); });
    return row;
  });
  return (
    <div className="space-y-6" data-testid="new-starters">
      <div className="panel p-5 sm:p-6">
        <h3 className="font-display text-lg font-semibold text-slate-100">New starter ramp-up</h3>
        <p className="text-xs text-slate-500 mb-4" data-testid="new-starters-summary">Weekly visits per hour worked from each new picker's first week · team average {fmtNum(teamAvg)} / hr (dashed) · {starters.length} new starters since {dayLabel(cut)} {cut.slice(0, 4)}</p>
        <ResponsiveContainer width="100%" height={320} initialDimension={{ width: 900, height: 320 }}>
          <LineChart data={chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1F2937" vertical={false} />
            <XAxis dataKey="label" stroke="#64748B" tick={{ fontSize: 11 }} tickLine={false} interval={1} />
            <YAxis stroke="#64748B" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
            <ReferenceLine y={teamAvg} stroke="#F59E0B" strokeDasharray="6 4" label={{ value: `team avg ${fmtNum(teamAvg)}`, fill: "#F59E0B", fontSize: 10, position: "insideTopRight" }} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {shown.map((s, i) => <Line key={s.id} dataKey={s.id} name={s.id} stroke={COLORS[i]} strokeWidth={2.25} dot={{ r: 2.5 }} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="panel overflow-x-auto" data-testid="new-starters-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800">{["Picker", "Started", "Weeks worked", "Reached team avg", "First 4 wks / hr", "Latest 4 wks / hr", "% of team avg"].map((h) => <th key={h} className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap first:pl-5">{h}</th>)}</tr></thead>
          <tbody>{starters.map((s) => (
            <tr key={s.id} data-testid={`starter-row-${slug(s.id)}`} onClick={() => onOpen(s.id)} className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer transition-colors">
              <td className="pl-5 py-2.5 text-slate-100 font-medium">{s.id}</td>
              <td className="px-4 font-mono text-slate-400 whitespace-nowrap">{dayLabel(s.start)} {s.start.slice(0, 4)}</td>
              <td className="px-4 font-mono text-slate-400">{s.weeksWorked}</td>
              <td className="px-4 font-mono whitespace-nowrap" data-testid={`starter-reached-${slug(s.id)}`}>{s.reached ? <span className="text-emerald-400">Week {s.reached}</span> : <span className="text-amber-300">Not yet</span>}</td>
              <td className="px-4 font-mono text-slate-300">{s.first4 == null ? "—" : fmtNum(s.first4)}</td>
              <td className="px-4 font-mono text-slate-100">{s.current == null ? "—" : fmtNum(s.current)}</td>
              <td className={`px-4 font-mono ${s.pct >= 100 ? "text-emerald-400" : "text-rose-300"}`}>{s.pct == null ? "—" : `${s.pct.toFixed(0)}%`}</td>
            </tr>
          ))}</tbody>
        </table>
        <p className="text-[11px] text-slate-500 px-5 py-3 border-t border-slate-800">"Reached team avg" = first of two consecutive weeks at or above the team average visits/hr. Weeks with under 2 hours worked are ignored. Chart shows up to {COLORS.length} most recent starters, first {MAX_WEEKS} weeks.</p>
      </div>
    </div>
  );
};
